import { defineField, defineType } from 'sanity'
import { BsBorderAll, BsList, BsTable } from 'react-icons/bs'
import { FiAlignLeft, FiImage, FiSidebar } from 'react-icons/fi'
import { GrApps } from 'react-icons/gr'
import { SplitHorizontalIcon } from '@sanity/icons'

// Caption fields for images
export const CaptionFields = [
  defineField({
    name: 'caption',
    title: 'Caption',
    type: 'text',
    options: {
      isHighlighted: true,
    },
    rows: 4,
  }),
  defineField({
    name: 'captionPositionX',
    title: 'Caption Position (X-axis)',
    type: 'string',
    options: {
      isHighlighted: true,
      list: [
        { title: 'Left', value: 'left' },
        { title: 'Right', value: 'right' },
      ],
      layout: 'radio',
      direction: 'horizontal',
    },
    initialValue: 'left',
  }),
  defineField({
    name: 'captionPositionY',
    title: 'Caption Position (Y-axis)',
    type: 'string',
    options: {
      isHighlighted: true,
      list: [
        { title: 'Top', value: 'top' },
        { title: 'Bottom', value: 'bottom' },
      ],
      layout: 'radio',
      direction: 'horizontal',
    },
    initialValue: 'top',
  }),
]

// Simple portable text components that are safe to migrate
export const PortableImage = {
  type: 'image',
  icon: FiImage,
  options: {
    hotspot: true,
  },
  fields: CaptionFields,
}

// AR-1823: labelled "legacy, deprecated" rather than left as a second "Table".
// It is NOT a supported alternative to the block below: inline legacy tables
// render as an empty bordered box today, because PT/Table.vue declares a `table`
// prop while the block supplies `rows`. That renderer bug predates this change
// and is deliberately left alone here (see the PR), but the label must not imply
// the block works. Display label only - no stored data changes, and none of the
// standalone `table` fields (tableBlock, compareTable, sizeTable, compare,
// buyerGuide) are affected.
export const PortableTable = {
  type: 'table',
  title: 'Table (legacy, deprecated)',
  icon: BsTable,
}

// AR-1823: the Studio's built-in Portable Text table editor - the one editors
// should reach for, so it takes the plain "Table" title.
export const PortableRichTable = {
  type: 'richTable',
  title: 'Table',
  icon: BsBorderAll,
}

export const PortableCode = { 
  type: 'code' 
}

export const PortableFeatures = { 
  type: 'featureList' 
}

export const PortableSpecs = defineType({
  name: 'specs',
  title: 'Specs',
  type: 'object',
  icon: BsList,
  preview: {
    prepare() {
      return { title: 'Specs' }
    },
  },
  fields: [
    defineField({
      name: 'items',
      title: 'Specs',
      type: 'array',
      of: [
        {
          type: 'object',
          name: 'spec',
          fields: [
            defineField({ 
              name: 'label', 
              title: 'Label', 
              type: 'string' 
            }),
            defineField({ 
              name: 'value', 
              title: 'Value', 
              type: 'string' 
            }),
          ],
        },
      ],
    }),
  ],
})

export const PortableTextAlign = defineType({
  icon: FiAlignLeft,
  name: 'textAlignment',
  title: 'Text Alignment',
  type: 'object',
  fields: [
    defineField({
      title: 'Content',
      name: 'content',
      type: 'array',
      of: [{ type: 'string' }],
    }),
    defineField({
      name: 'alignment',
      title: 'Alignment',
      type: 'string',
      options: {
        list: [
          { title: 'Left', value: 'left' },
          { title: 'Right', value: 'right' },
          { title: 'Center', value: 'center' },
        ],
      },
    }),
  ],
})

export const PortableReadMore = defineType({
  name: 'readMore',
  type: 'object',
  title: 'Read More',
  icon: SplitHorizontalIcon,
  fields: [
    defineField({
      name: 'breakpoint',
      type: 'boolean',
      title: 'Read More Breakpoint',
      description:
        'This marks where content will be split for the "Read More" functionality. Use only once per field.',
      initialValue: true,
      readOnly: true,
      hidden: false,
    }),
  ],
  preview: {
    prepare() {
      return {
        title: '-- Read More --',
      }
    },
  },
})

// Vehicle embeds inside an article body (AR-1841).
//
// These are WRAPPERS, not the page blocks themselves: the pre-migration studio
// nested the real `vehicleAlbum` / `vehicleGallery` object under a `content`
// field, so every article authored back then stores
//   { _type: 'vehicleAlbum', content: { _type: 'vehicleAlbum', album: {_ref} } }
// (182 such blocks across 125 published articles). The shape has to be declared
// exactly as stored, otherwise the studio rejects the whole body with "Invalid
// Portable Text value" and offers editors a "Remove the block" button that would
// silently drop published content.
export const PortableVehicleAlbum = defineType({
  name: 'vehicleAlbum',
  title: 'Vehicle Album',
  type: 'object',
  icon: FiSidebar,
  fields: [
    defineField({
      name: 'content',
      title: 'Album',
      type: 'vehicleAlbum',
    }),
    ...CaptionFields,
  ],
  preview: {
    select: {
      make: 'content.album.vehicle.make.name',
      model: 'content.album.vehicle.model.name',
      generation: 'content.album.vehicle.generation.name',
      color: 'content.album.color.name',
      hide: 'content.album.hide',
      media: 'content.album.images.0.asset',
    },
    prepare({ make = '', model = '', generation = '', color = '', hide = false, media }) {
      const title = `${color} ${make} ${model} ${generation}`.trim()

      return {
        title: title ? `${hide ? '[Hidden] ' : ''}${title}` : 'New Album',
        subtitle: 'Vehicle album',
        media,
      }
    },
  },
})

export const PortableVehicleGallery = defineType({
  name: 'vehicleGallery',
  title: 'Vehicle Gallery',
  type: 'object',
  icon: GrApps,
  fields: [
    defineField({
      name: 'content',
      title: 'Gallery',
      type: 'vehicleGallery',
    }),
  ],
  preview: {
    select: {
      title: 'content.title',
    },
    prepare({ title }) {
      return {
        title: title || 'Vehicle gallery',
        subtitle: 'Vehicle gallery',
      }
    },
  },
})
